import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CCAuthService } from '@maids/cc-erp-services';
import { first, Subscription } from 'rxjs';
import {
  OAuthAuthorizationContext,
  OAuthAuthorizationService,
  OAuthConsentMetadata,
} from '../../services/oauth-authorization.service';
import { environment } from 'src/environments/environment';
import { assets } from '@maids/cc-lib/utils';

type ConsentViewState = 'loading' | 'ready' | 'submitting' | 'error';

@Component({
  selector: 'app-oauth-consent',
  templateUrl: './oauth-consent.component.html',
  styleUrls: ['./oauth-consent.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OauthConsentComponent implements OnInit, OnDestroy {
  viewState: ConsentViewState = 'loading';
  errorMessage = '';
  metadata: OAuthConsentMetadata | null = null;

  private _context: OAuthAuthorizationContext | null = null;
  private readonly _subscriptions = new Subscription();

  constructor(
    private readonly _oauthAuthorization: OAuthAuthorizationService,
    private readonly _auth: CCAuthService,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this._oauthAuthorization.captureFromQueryParams(
      this._route.snapshot.queryParams
    );
    this._context = this._oauthAuthorization.getStoredContext();
    if (!this._context) {
      this.showError(
        'This authorization request is missing or invalid. Start again from the application that sent you here.'
      );
      return;
    }

    this._subscriptions.add(
      this._auth.loggedUser$.pipe(first()).subscribe((user) => {
        if (!user) {
          this._router.navigate(['/login']);
          return;
        }
        this.loadMetadata();
      })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  allowAccess(): void {
    this.submitDecision('approve');
  }

  denyAccess(): void {
    this.submitDecision('deny');
  }

  cancel(): void {
    this._oauthAuthorization.clearContext();
    this._router.navigate(['/login']);
  }

  imageUrl(path: string): string {
    return Boolean(environment.production) && !Boolean(environment.newErp)
      ? assets.getBasePath() + `new-theme-pages/assets/images/${path}`
      : assets.getBasePath() + `assets/images/${path}`;
  }

  private loadMetadata(): void {
    if (!this._context) {
      return;
    }

    this.viewState = 'loading';
    this._cdr.markForCheck();

    this._subscriptions.add(
      this._oauthAuthorization.loadConsentMetadata(this._context).subscribe({
        next: (metadata) => {
          this.metadata = metadata;
          this.viewState = 'ready';
          this._cdr.markForCheck();
        },
        error: (error) => {
          const message =
            error?.error?.message ||
            error?.error?.error_description ||
            error?.message ||
            'Unable to load authorization details. The request may have expired.';
          this.showError(message);
        },
      })
    );
  }

  private submitDecision(action: 'approve' | 'deny'): void {
    if (!this._context || this.viewState === 'submitting') {
      return;
    }

    this.viewState = 'submitting';
    this.errorMessage = '';
    this._cdr.markForCheck();

    const request$ =
      action === 'approve'
        ? this._oauthAuthorization.approve(
            this._context,
            this.metadata?.csrfNonce
          )
        : this._oauthAuthorization.deny(this._context, this.metadata?.csrfNonce);

    this._subscriptions.add(
      request$.subscribe({
        next: (response) => {
          try {
            this._oauthAuthorization.completeWithRedirect(response);
          } catch {
            this.showError(
              'Authorization completed but no redirect URL was returned. Please try again.'
            );
          }
        },
        error: (error) => {
          const message =
            error?.error?.message ||
            error?.error?.error_description ||
            error?.message ||
            'Unable to complete authorization. The request may have expired.';
          this.showError(message);
        },
      })
    );
  }

  private showError(message: string): void {
    this.viewState = 'error';
    this.errorMessage = message;
    this._cdr.markForCheck();
  }
}
